import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CATEGORIES } from '@/types/movie';

interface SearchBarProps {
  size?: 'md' | 'lg';
  defaultValue?: string;
  placeholder?: string;
}

export default function SearchBar({
  size = 'md',
  defaultValue = '',
  placeholder = '搜索影片标题、简介或标签…',
}: SearchBarProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(defaultValue || searchParams.get('q') || '');
  const [showSuggest, setShowSuggest] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const suggestions = ['午夜电台', '末班地铁', '黑水灯塔', '镜中第七夜', '悬疑', '恐怖', '剧情'];

  const submit = (kw: string) => {
    const q = kw.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setShowSuggest(false);
  };

  const lg = size === 'lg';

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(value);
        }}
        className={`flex items-center gap-2 rounded-full border border-background-300/70 bg-background-100/80 pl-4 pr-1.5 transition focus-within:border-primary-400 ${
          lg ? 'h-14' : 'h-11'
        }`}
      >
        <i className={`ri-search-line text-foreground-400 ${lg ? 'text-xl' : 'text-base'}`}></i>
        <input
          type="text"
          name="q"
          value={value}
          onFocus={() => setShowSuggest(true)}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-transparent text-foreground-950 placeholder:text-foreground-400 focus:outline-none ${
            lg ? 'text-base' : 'text-sm'
          }`}
        />
        <button
          type="submit"
          className={`shrink-0 whitespace-nowrap rounded-full bg-primary-500 font-medium text-white transition hover:bg-primary-600 ${
            lg ? 'h-11 px-6 text-sm' : 'h-8 px-4 text-xs'
          }`}
        >
          搜索
        </button>
      </form>

      {showSuggest && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-lg border border-background-300/70 bg-background-100 p-2">
          <p className="px-3 py-1.5 text-xs text-foreground-500">热门搜索</p>
          <div className="flex flex-wrap gap-2 px-2 pb-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setValue(s);
                  submit(s);
                }}
                className="rounded-full bg-secondary-100/80 px-3 py-1 text-xs text-secondary-900 transition hover:bg-secondary-200"
              >
                {s}
              </button>
            ))}
          </div>
          <div className="border-t border-background-300/50 pt-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                type="button"
                onClick={() => {
                  navigate(`/category/${encodeURIComponent(cat.key)}`);
                  setShowSuggest(false);
                }}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground-700 transition hover:bg-background-200/70"
              >
                <i className={`${cat.icon} text-primary-400`}></i>
                进入「{cat.label}」分类
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}